import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Typography,
  Divider,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import CheckoutForm from '../subscription/components/CheckoutForm';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { API } from 'aws-amplify';
import useSnack from 'hooks/useSnack';
import Spin from 'components/common/Spin';
import {
  GET_ADDONS_BY_TYPE,
  GET_SUBSCRIPTIONS,
} from 'graphql/products';
import { Query, useQuery } from 'react-apollo';
import CloseIcon from '@material-ui/icons/Close';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUB);

const useStyles = makeStyles((theme) => ({
  cardContent: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  list: {
    'listStyle': 'none',
    '& li': {
      'display': 'flex',
      'marginBottom': theme.spacing(1),
      '& svg': {
        color: theme.palette.success.main,
        fontSize: 20,
        marginRight: theme.spacing(1),
      },
    },
  },
}));

const Upsell = ({ student, userType }) => {
  const snack = useSnack();
  const classes = useStyles();
  const [showPaymentDialog, setShowPaymentDialog] = React.useState(false);
  const [amount, setAmount] = React.useState(0);
  const [credits, setCredits] = React.useState({});
  const [addonData, setAddonData] = React.useState({});
  const [success, setSuccess] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState(null);
  const { data } = useQuery(GET_SUBSCRIPTIONS, {
    variables: { studentId: student.id },
  });
  const subscriptions = data?.subscriptions;

  const getPaymentMethod = async () => {
    if (subscriptions && subscriptions.length > 0) {
      try {
        return await API.post('api', '/stripe/payment/methods', {
          body: {
            customer: subscriptions[0]?.stripe_customer_id,
          },
        }).then((res) => {
          setPaymentMethod(res.data);
        });
      } catch (e) {
        console.log(e.message);
      }
    }
  };

  React.useEffect(() => {
    getPaymentMethod();
  }, [subscriptions]);

  const onCardSubmit = async (token) => {
    try {
      await API.post('api', '/stripe/charge/create', {
        body: {
          token: token,
          amount: amount * 100,
          customer: subscriptions[0]?.stripe_customer_id,
          credits: credits,
          userId: student.id,
          extension_days: addonData.extension_days,
          name: addonData.package_name,
          description: addonData.addon_info,
        },
      }).then(() => {
        setSuccess(true);
        window.analytics.track(`Purchase from the add-ons page`, {
          amount: amount * 100,
          customer: subscriptions[0]?.stripe_customer_id,
          credits: credits,
          userId: student.id,
          extension_days: addonData.extension_days,
          name: addonData.package_name,
          description: addonData.addon_info,
          ...student,
        });
        snack.success('Success!');
      });
    } catch (e) {
      snack.error(e.message);
    }
  };

  const handlePurchase = (addonInfo) => {
    if (student.is_trial) {
      return snack.info(
        'Add-ons are not available for free trial users. To gain access, please upgrade your account by purchasing one of our programs!',
      );
    }

    setAddonData(addonInfo);
    setSuccess(false);
    setAmount(addonInfo.price);
    setCredits(addonInfo.credits);

    setShowPaymentDialog(true);
  };

  return (
    <Box marginTop={4}>
      <Query
        query={GET_ADDONS_BY_TYPE}
        variables={{ jsonFilter: { value: student.product_type } }}
      >
        {({ data, loading, refetch }) => {
          if (loading) return <Spin linear spinning />;

          const addons = data.addons;
          const forAllStudents = addons.filter(
            (f) => f.class_year.length === 0,
          );

          return (
            <Grid container spacing={3} direction="column" alignItems="center">
              {addons
                .filter(
                  (m) =>
                    m.product_type.some(
                      (n) => n.value === student.product_type,
                    ) && m.class_year.some((n) => n === student.class_year),
                )
                .concat(forAllStudents)
                .map((a) => {
                  return (
                    <Grid item md={6} style={{ width: '100%' }}>
                      <Card>
                        <CardContent className={classes.cardContent}>
                          <Box marginBottom={4}>
                            <Typography variant="h5">
                              {a.package_name}
                            </Typography>
                          </Box>
                          <Box>
                            <Box
                              marginBottom={3}
                              dangerouslySetInnerHTML={{ __html: a.addon_info }}
                            />
                            <Divider />
                            <Box
                              marginTop={3}
                              display="flex"
                              alignItems="flex-end"
                              justifyContent="center"
                              marginBottom={3}
                            >
                              <Typography variant="h6">$</Typography>&nbsp;
                              <Typography variant="h3">
                                {`${a.price}`}
                              </Typography>
                            </Box>
                            <Box textAlign="center">
                              <Button
                                size="large"
                                color="primary"
                                variant="contained"
                                onClick={() => handlePurchase(a)}
                              >
                                Purchase
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>
          );
        }}
      </Query>
      {showPaymentDialog && (
        <Dialog
          onBackdropClick={() => setShowPaymentDialog(false)}
          open
          fullWidth
          maxWidth={'sm'}
        >
          <Elements stripe={stripePromise}>
            <Card>
              <Box textAlign="right">
                <Button onClick={() => setShowPaymentDialog(false)}>
                  <CloseIcon color="secondary" />
                </Button>
              </Box>
              <CardContent>
                {success ? (
                  <Box textAlign="center">
                    <Typography color="primary" variant="subtitle1">
                      {' '}
                      {`Thank you for your purchase! This add-on has been applied to ${
                        userType === 'student' ? 'your' : student.fname+"'s"
                      } account.`}{' '}
                    </Typography>
                  </Box>
                ) : (
                  <CheckoutForm
                    onToken={onCardSubmit}
                    isUpsell={paymentMethod?.length > 0 ? true : false}
                    paymentMethod={paymentMethod}
                  />
                )}
              </CardContent>
            </Card>
          </Elements>
        </Dialog>
      )}
    </Box>
  );
};

export default Upsell;
